import React, { useState, useEffect } from "react";
import { collection, getDocs, doc, deleteDoc } from "firebase/firestore";
import { ref, deleteObject } from "firebase/storage";
import { db, storage } from "../firebase/config";
import toast from "react-hot-toast";
import DataTable from "react-data-table-component";
import { Edit, Trash2, ExternalLink, Plus, Image } from "lucide-react";
import AIToolsForm from "./AIToolsForm";
import { AIToolsTableStyles } from "../utils/customStyles";

const AIToolsTable = () => {
  const [tools, setTools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterText, setFilterText] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingTool, setEditingTool] = useState(null);

  const fetchTools = async () => {
    try {
      setLoading(true);
      const querySnapshot = await getDocs(collection(db, "aitools"));
      const toolsData = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
        createdAt:
          doc.data().createdAt?.toDate?.() ||
          new Date(doc.data().createdAt || 0),
      }));
      setTools(toolsData);
    } catch (error) {
      toast.error("Erreur lors du chargement des outils");
      console.error("Error fetching tools:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTools();
  }, []);

  const deleteLogoFromStorage = async (logoUrl) => {
    if (!logoUrl || !logoUrl.includes('firebase')) return;
    
    try {
      // Extract the path from the Firebase Storage URL
      const url = new URL(logoUrl);
      const pathSegments = url.pathname.split('/');
      const encodedPath = pathSegments[pathSegments.length - 1];
      const decodedPath = decodeURIComponent(encodedPath);
      
      // Remove the token parameter if present
      const path = decodedPath.split('?')[0];
      
      const logoRef = ref(storage, path);
      await deleteObject(logoRef);
    } catch (error) {
      console.error("Error deleting logo from storage:", error);
      // Don't throw error as the document deletion is more important
    }
  };

  const handleDelete = async (id, name, logoUrl) => {
    if (window.confirm(`Êtes-vous sûr de vouloir supprimer "${name}"?`)) {
      try {
        // Delete the logo from storage first
        if (logoUrl) {
          await deleteLogoFromStorage(logoUrl);
        }
        
        // Delete the document from Firestore
        await deleteDoc(doc(db, "aitools", id));
        setTools(tools.filter((tool) => tool.id !== id));
        toast.success("Outil supprimé avec succès!");
      } catch (error) {
        toast.error("Erreur lors de la suppression");
        console.error("Error deleting tool:", error);
      }
    }
  };

  const handleEdit = (tool) => {
    setEditingTool(tool);
    setIsModalOpen(true);
  };

  const handleAddNew = () => {
    setEditingTool(null);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingTool(null);
  };

  const handleFormSuccess = () => {
    fetchTools();
    handleCloseModal();
  };

  const columns = [
    {
      name: "Logo",
      cell: (row) => (
        <div className="flex items-center justify-center p-1">
          {row.logo ? (
            <img
              src={row.logo}
              alt={`${row.name} logo`}
              className="w-10 h-10 object-cover rounded border border-gray-600"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'flex';
              }}
            />
          ) : (
            <div className="w-10 h-10 bg-gray-700 rounded border border-gray-600 flex items-center justify-center">
              <Image size={16} className="text-gray-400" />
            </div>
          )}
          <div 
            className="w-10 h-10 bg-gray-700 rounded border border-gray-600 items-center justify-center" 
            style={{ display: 'none' }}
          >
            <Image size={16} className="text-gray-400" />
          </div>
        </div>
      ),
      width: '80px',
      center: true,
    },
    {
      name: "Nom",
      selector: (row) => row.name,
      sortable: true,
      cell: (row) => (
        <div className="font-medium text-white" title={row.name}>
          {row.name}
        </div>
      ),
    },
    {
      name: "Description",
      selector: (row) => row.description,
      sortable: true,
      cell: (row) => (
        <div className="text-gray-200 text-left truncate text-sm" title={row.description}>
          {row.description?.length > 80
            ? `${row.description.substring(0, 80)}...`
            : row.description}
        </div>
      ),
    },
    {
      name: "Lien",
      selector: (row) => row.link,
      sortable: true,
      cell: (row) => (
        <a
          href={row.link}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-300 hover:text-blue-100 text-sm flex items-center gap-1"
          title={row.link}
        >
          <span className="truncate max-w-[200px]">{row.link}</span>
          <ExternalLink size={12} />
        </a>
      ),
    },
    {
      name: "Catégories",
      selector: (row) => row.categories?.join(", ") || "",
      sortable: true,
      cell: (row) => (
        <div className="flex flex-wrap gap-1">
          {row.categories?.slice(0, 2).map((category) => (
            <span
              key={category}
              className="bg-[#10227a] text-gray-100 text-[#fffffffb] px-2 py-1 rounded text-xs"
            >
              {category}
            </span>
          ))}
          {row.categories?.length > 2 && (
            <span className="text-gray-400 text-xs">
              +{row.categories.length - 2}
            </span>
          )}
        </div>
      ),
    },
    
    {
      name: "Actions",
      cell: (row) => (
        <div className="flex gap-2">
          <button
            onClick={() => handleEdit(row)}
            className="p-2 bg-[#1738c2] text-white rounded hover:bg-[#1e45ff] transition-colors"
            title="Modifier"
          >
            <Edit size={14} />
          </button>
          <button
            onClick={() => handleDelete(row.id, row.name, row.logo)}
            className="p-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
            title="Supprimer"
          >
            <Trash2 size={14} />
          </button>
        </div>
      ),
    },
  ];

  const filteredItems = tools.filter(
    (item) =>
      item.name?.toLowerCase().includes(filterText.toLowerCase()) ||
      item.description?.toLowerCase().includes(filterText.toLowerCase()) ||
      item.categories?.some((cat) =>
        cat.toLowerCase().includes(filterText.toLowerCase())
      )
  );


  return (
    <div className="bg-[#081245] text-white min-h-screen rounded-lg border border-gray-600 p-6">
      <div className="mx-auto">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold">Gestion des Outils IA</h1>
          <div className="text-sm text-gray-200">
            {filteredItems.length} outil{filteredItems.length !== 1 ? "s" : ""}
            {filterText &&
              ` (filtré${filteredItems.length !== 1 ? "s" : ""} sur ${
                tools.length
              })`}
          </div>
        </div>
        <div className="mb-4 flex justify-between items-center gap-4 flex-col md:flex-row">
          <input
            type="text"
            placeholder="Rechercher..."
            value={filterText}
            onChange={(e) => setFilterText(e.target.value)}
            className="w-full md:flex-1 p-3 bg-[#081245] rounded border border-gray-500 focus:border-blue-300 focus:outline-none text-white"
          />
          <button
            onClick={handleAddNew}
            className="mt-2 md:mt-0 flex items-center gap-2 px-4 py-3 bg-[#1738c2] text-white rounded hover:bg-[#1e45ff] transition-colors font-medium"
          >
            <Plus size={16} />
            Ajouter un outil
          </button>
        </div>
        <div className="bg-[#081245] rounded-lg border border-gray-800 overflow-hidden">
          <DataTable
            columns={columns}
            data={filteredItems}
            pagination
            paginationPerPage={10}
            paginationRowsPerPageOptions={[5, 10, 25, 50]}
            progressPending={loading}
            progressComponent={
              <div className="p-8 text-center text-white">
                Chargement des outils...
              </div>
            }
            noDataComponent={
              <div className="p-8 text-center text-gray-400">
                {filterText ? (
                  <>
                    <p className="text-xl mb-4">
                      Aucun outil trouvé pour "{filterText}"
                    </p>
                    <button
                      onClick={() => setFilterText("")}
                      className="text-blue-300 hover:text-blue-200"
                    >
                      Effacer la recherche
                    </button>
                  </>
                ) : (
                  <>
                    <p className="text-xl mb-4">Aucun outil trouvé</p>
                    <p className="mb-4">
                      Ajoutez votre premier outil IA pour commencer
                    </p>
                    <button
                      onClick={handleAddNew}
                      className="px-4 py-2 bg-[#1738c2] text-white rounded hover:bg-[#1e45ff] transition-colors"
                    >
                      Ajouter un outil IA
                    </button>
                  </>
                )}
              </div>
            }
            customStyles={AIToolsTableStyles}
            defaultSortFieldId="createdAt"
            defaultSortAsc={false}
            responsive
            highlightOnHover
            pointerOnHover
          />
        </div>
        {isModalOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-[#081245] rounded-lg border border-gray-800 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-xl font-bold text-white">
                    {editingTool
                      ? "Modifier l'outil IA"
                      : "Ajouter un nouvel outil IA"}
                  </h2>
                  <button
                    onClick={handleCloseModal}
                    className="text-gray-400 hover:text-white text-2xl"
                  >
                    ×
                  </button>
                </div>
                <AIToolsForm
                  editingTool={editingTool}
                  onSuccess={handleFormSuccess}
                  onCancel={handleCloseModal}
                  isModal={true}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AIToolsTable;